import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaxDurationShareButton from 'blocks/DownloadPage/components/MaxDurationShareButton';
import { BlueskyButton } from 'components/SocialButton';
import useBlueskyAuthenticator from 'hooks/useBlueskyAuthenticator';
import useOnMount from 'hooks/useOnMount';
import useQuery from 'hooks/useQuery';
import { videoDurationSecondsSelector } from 'redux/modules/download';
import { hideModal, pushModal } from 'redux/modules/modal';
import {
  blueskyUsernameSelector,
  isLoadingBlueskyUserSelector,
} from 'redux/modules/social';
import { createChainedFunction } from 'utils/functions';
import useOnDownloadPageAction from '../useOnDownloadPageAction';
import { MAX_VIDEO_DURATION_SEC } from './utils';

const BlueskyPostButton: React.FC = () => {
  const [shouldReauthenticate, setShouldReauthenticate] = useState<boolean>(
    false,
  );

  const isLoadingBlueskyUser = useSelector(isLoadingBlueskyUserSelector);
  const blueskyUsername = useSelector(blueskyUsernameSelector);
  const videoDurationSec = useSelector(videoDurationSecondsSelector);

  const { onDownloadPageAction } = useOnDownloadPageAction();
  const { provider } = useQuery();
  const dispatch = useDispatch();

  const handleError = useCallback((): void => {
    setShouldReauthenticate(true);
    dispatch(hideModal());
  }, [dispatch]);

  const {
    authenticating,
    authenticate,
    withAuthentication,
  } = useBlueskyAuthenticator({
    force: shouldReauthenticate,
    appRedirectUrl: window.location.href,
    onError: handleError,
    defaultAuthenticated: !!blueskyUsername,
  });

  const isAuthenticating = authenticating || isLoadingBlueskyUser;

  const openSocialShareModal = useCallback(() => {
    dispatch(
      pushModal({
        name: 'SocialShare',
        params: {
          platform: 'bluesky',
        },
      }),
    );
  }, [dispatch]);

  useOnMount(() => {
    if (provider === 'bluesky') {
      dispatch(
        pushModal({
          name: 'SocialShare',
          params: {
            platform: 'bluesky',
          },
        }),
      );
    }
  });

  useEffect(() => {
    if (shouldReauthenticate && !authenticating) {
      onDownloadPageAction('blueskyShare');
      authenticate();
    }
  }, [
    authenticate,
    authenticating,
    onDownloadPageAction,
    shouldReauthenticate,
  ]);

  return (
    <MaxDurationShareButton
      platform="Bluesky"
      videoDurationSec={videoDurationSec}
      maxVideoDurationSec={MAX_VIDEO_DURATION_SEC}
    >
      <BlueskyButton
        size="small"
        disabled={isAuthenticating}
        onClick={createChainedFunction(
          () => onDownloadPageAction('blueskyShare'),
          withAuthentication(openSocialShareModal, shouldReauthenticate),
        )}
      />
    </MaxDurationShareButton>
  );
};

export default BlueskyPostButton;
